import { clsx } from 'clsx';
import { type ComponentPropsWithRef, type ReactNode } from 'react';

import { noop } from '@repo/lib/functions';

import s from '../Input.module.css';

export type InputGroupProps = ComponentPropsWithRef<'div'> & {
  className?: string;
  children?: ReactNode;
};

export const InputGroup = (props: InputGroupProps) => {
  const { children, className, onClick, ...attrs } = props;

  const handleInteraction: ComponentPropsWithRef<'div'>['onClick'] = (e) => {
    // This handleInteraction function is used to handle clicks on the input group.
    // It checks if the clicked element is an interactive element (like a button or link).
    // If it is not, it focuses the first input element inside the group.
    // This is useful for accessibility, as it allows users to click anywhere in the group to focus the input.
    // For keyboard access, we reply on the inputs to handle their own key events, do we use a no-op function here.
    const element = e.target as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    const interactiveElements = [
      'a',
      'button',
      'input',
      'textarea',
      'select',
      'label',
    ];

    const isInteractive = interactiveElements.includes(tagName);

    const firstInput = e.currentTarget.querySelector<
      HTMLInputElement | HTMLTextAreaElement
    >('input, textarea');

    if (firstInput && !isInteractive) {
      firstInput.focus();
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <div
      className={clsx(s.group, className)}
      onClick={handleInteraction}
      onKeyDown={noop} // We need a no-op function to prevent TypeScript errors on interactive elements.
      {...attrs}
    >
      {children}
    </div>
  );
};
